/**
 * **手势期间的指针跟踪** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 状态机自己不收指针事件。有几个状态必须**自己跟指针**（因为松手那一刻
 * 图引擎给不出坐标）：
 *
 *   `armedDetach` / `detached` —— 「从已连线的输入拖出」那条路径
 *   `lasso`                    —— 划痕
 *   `linking`                  —— 连线本身还是图引擎在管，但状态机要记住
 *                                 松手落在哪（`connectEnd` 不带坐标），
 *                                 否则「松手弹候选菜单」会弹在起点端口上
 *
 * `watch(gestureNeedsPointer, ..., { flush: 'sync' })` 负责在进入这些状态时
 * 挂上 window 监听、离开时摘掉。
 *
 * 另外还有 `connectableFor`（本节点上可作为落点的端口）和
 * `repointConnectionLine`（把连线预览重新指到某个端口）。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `sendGesture` 声明得比这段晚 —— 但只在事件回调里调用，所以闭包注入即可。
 */
import { computed, watch } from 'vue'
import { connectableHandles } from '@/graph/linkRules'
import type { GestureState } from '@/graph/canvasGesture'
import type { SocketType } from '@/types/socket'
import type { GraphNodeModel } from '@/types/node'
import type { FlowStoreApi } from '@/graph/flowStore'
import { FLOW_DOM, HANDLE_ID_ATTR, HANDLE_NODE_ATTR } from '@/graph/graphDom'

export interface PointerTrackingCtx {
  gesture: { value: GestureState }
  flowStore: { value: FlowStoreApi | null }
  linking: { value: boolean }
  linkingNodeId: { value: string | null }
  linkingHandleId: { value: string | null }
  linkingSide: { value: 'source' | 'target' }
  /**
   * 在**当前焦点 Area 的画布**里查元素。
   *
   * ⚠️ 不要用 `document.querySelector('.np-graph__handle[…]')`：
   * 两个节点图窗口看的是同一份文档、节点与端口 id 相同，
   * document 级查询命中的是**文档顺序在前**的那个窗口 ——
   * 于是从「已连线的输入」往外拖时，读到的是另一个窗口的端口方位。
   */
  queryGraph: <T extends Element = Element>(selector: string) => T | null
  currentModels: () => GraphNodeModel[]
  edgeViews: () => {
    source: string
    sourceHandle?: string | null
    target: string
    targetHandle?: string | null
  }[]
  /** 手势状态机的事件入口（声明得比这段晚，用闭包） */
  sendGesture: (event: unknown) => void
}

export function usePointerTracking(ctx: PointerTrackingCtx) {
  const {
    gesture,
    flowStore,
    linking,
    linkingNodeId,
    linkingHandleId,
    linkingSide,
    queryGraph,
    currentModels,
    edgeViews,
    sendGesture,
  } = ctx

/* ── 手势期间的指针跟踪 ─────────────────────────────────────── */

/**
 * 这几个状态需要自己跟指针：
 *   armedDetach / detached —— 「从已连线的输入拖出」那条路径
 *   lasso                  —— 划痕
 *   linking                —— **连线本身还是图引擎在管**，但状态机要记住
 *                             松手落在哪（connectEnd 不带坐标），
 *                             否则「松手弹候选菜单」会弹在起点端口上
 */
const gestureNeedsPointer = computed(() => {
  const kind = gesture.value.kind
  return (
    kind === 'linking' ||
    kind === 'armedDetach' ||
    kind === 'detached' ||
    kind === 'lasso'
  )
})

function onGestureMove(ev: PointerEvent) {
  sendGesture({
    type: 'pointerMove',
    point: { x: ev.clientX, y: ev.clientY },
    buttons: ev.buttons,
  })
}

function onGestureUp(ev: PointerEvent) {
  sendGesture({ type: 'pointerUp', point: { x: ev.clientX, y: ev.clientY } })
}

function onGestureCancel() {
  sendGesture({ type: 'pointerCancel' })
}

// flush: 'sync' —— 挂起状态一出现就要开始收指针事件，晚一拍会漏掉开头几像素
watch(
  gestureNeedsPointer,
  (on) => {
    if (on) {
      window.addEventListener('pointermove', onGestureMove)
      window.addEventListener('pointerup', onGestureUp)
      window.addEventListener('pointercancel', onGestureCancel)
    } else {
      window.removeEventListener('pointermove', onGestureMove)
      window.removeEventListener('pointerup', onGestureUp)
      window.removeEventListener('pointercancel', onGestureCancel)
    }
  },
  { flush: 'sync' },
)

/**
 * 本节点上可作为当前连线落点的端口 id 列表。
 *
 * 判定逻辑在 graph/linkRules.ts（纯函数、有单测）；这里只负责把响应式状态喂进去。
 */
function connectableFor(nodeId: string): string[] | null {
  const startNodeId = linkingNodeId.value
  const startHandleId = linkingHandleId.value
  if (!startNodeId || !startHandleId) return null

  return connectableHandles({
    models: currentModels(),
    edges: edgeViews(),
    startNodeId,
    startHandleId,
    startSide: linkingSide.value === 'target' ? 'target' : 'source',
    nodeId,
  })
}

/**
 * 把连线的**视觉起点**挪到原来的输出端口。
 *
 * 背景：从已连接的输入端口往外拖时，图引擎的连线是从那个**输入**端口画的，
 * 看上去像「从输入找输出」，很别扭。正确的语义是「把这根线从原输出端拽下来」。
 *
 * 能这么做，是因为图引擎把两件事分开了：
 *   视觉 → connectionStartHandle
 *   校验 → pointerdown 时闭包捕获的 fromHandle（不受影响）
 * 所以只动 connectionStartHandle 就能只改视觉、不动校验。
 */
function repointConnectionLine(
  sourceNodeId: string,
  sourceHandleId: string | null,
) {
  const store = flowStore.value
  if (!store?.startConnection || !sourceHandleId) return

  // handle 的方位从 DOM 上读（data-handlepos），别自己假设
  /* ⚠️ 选择器与属性名都从 `graphDom.ts` 取 —— 那是引擎的 DOM 契约 */
  const el = queryGraph(
    `.${FLOW_DOM.handle}[${HANDLE_NODE_ATTR}="${sourceNodeId}"][${HANDLE_ID_ATTR}="${sourceHandleId}"]`,
  )
  store.startConnection({
    nodeId: sourceNodeId,
    id: sourceHandleId,
    type: 'source',
    position: el?.getAttribute('data-handlepos') ?? 'right',
  })
}


  return {
    gestureNeedsPointer,
    onGestureMove,
    onGestureUp,
    onGestureCancel,
    connectableFor,
    repointConnectionLine,
  }
}
